import { getDefaultStore } from 'jotai'
import { DateTime } from 'luxon'
import { format } from 'date-fns'
import { storesDictAtom, userStoreAtom } from '../global/AtomsProvider'

/**
 * Pure timezone utility functions.
 * These functions access atoms at runtime via getDefaultStore(), which avoids
 * circular dependency issues at module load time.
 */

// Firestore Timestamp type for type checking
interface FirestoreTimestamp {
  toDate: () => Date
  seconds: number
  nanoseconds: number
}

/**
 * Normalizes a date value to a JavaScript Date object.
 * Handles Firestore Timestamps (with toDate()), plain Date objects, and ISO strings.
 * This is essential during the Firestore → Supabase migration since data may come
 * in different formats depending on the source.
 *
 * @param dateValue - The date value to normalize (Timestamp, Date, string, or null/undefined)
 * @returns JavaScript Date object, or null if input is null/undefined
 */
export const normalizeDate = (dateValue: FirestoreTimestamp | Date | string | null | undefined): Date | null => {
  if (dateValue == null) return null
  // Firestore Timestamp has toDate() method
  if (typeof (dateValue as FirestoreTimestamp).toDate === 'function') {
    return (dateValue as FirestoreTimestamp).toDate()
  }
  // Already a Date object
  if (dateValue instanceof Date) {
    return dateValue
  }
  // ISO string or other string format
  if (typeof dateValue === 'string') {
    return new Date(dateValue)
  }
  return null
}

// Runtime store timezone structure (not fully typed in AtomStore)
interface StoreTimezone {
  utc?: string[]
  offset?: number
}

export interface StoreWithTimezone {
  id?: string
  timezone?: StoreTimezone | string | null
  autoCloseShiftTime?: { value: number } | null
  [key: string]: unknown
}

// Type guard to check if timezone is an object with utc array
function hasTimezoneUtc(
  timezone: StoreTimezone | string | null | undefined
): timezone is StoreTimezone & { utc: string[] } {
  return (
    typeof timezone === 'object' &&
    timezone !== null &&
    'utc' in timezone &&
    Array.isArray(timezone.utc) &&
    timezone.utc.length > 0
  )
}

/**
 * The store's local wall clock for an instant, given the store record itself.
 * `getLocalStoreDate` is this plus the storeId → store lookup; range inversion needs the
 * store-object form because it already holds the specific store that framed an edge.
 */
const localDateInStore = (store: StoreWithTimezone | undefined, date: Date): Date => {
  const storeToday = new Date(date)
  if (!store || !hasTimezoneUtc(store.timezone)) {
    return storeToday
  }

  // use luxon to account for timezones and DST
  const systemTime = DateTime.local()
  const rezoned = systemTime.setZone(store.timezone.utc[0], { keepLocalTime: true })
  const offsetDifferenceInMinutes = systemTime.diff(rezoned, 'minutes').minutes
  const requiredOffsetChangeInMinutes = storeToday.getMinutes() + offsetDifferenceInMinutes
  storeToday.setMinutes(requiredOffsetChangeInMinutes)
  return storeToday
}

/**
 * The store's local CALENDAR DAY for an instant, as `yyyy-MM-dd`, read in the store's own zone
 * AT THAT INSTANT.
 *
 * Deliberately not `format(localDateInStore(...), 'yyyy-MM-dd')`. `localDateInStore` derives its
 * offset from `DateTime.local()` — the offset in effect NOW — and applies it to a date that may
 * be months away. For a zone that observes DST that is wrong by an hour whenever the date sits on
 * the other side of a transition from today, and an hour is a whole DAY when the instant is local
 * midnight, which every range bound is. Measured: `America/New_York` midnight on 2026-07-01
 * encodes to 2026-07-01T04:00:00Z; reading it back through the now-offset in January returns
 * 2026-06-30 — the ATL-1112 defect, reintroduced one report at a time. 35 stores across 31 live
 * orgs are in DST zones. Asia/Almaty (the org the ticket came from) has none, which is why
 * neither the specs nor the production spot-checks could see it.
 *
 * `DateTime.fromJSDate(...).setZone(zone)` resolves the offset for the instant being read, which
 * is the same rule `DateTime.fromObject(..., { zone })` uses to BUILD the bound — so this is the
 * exact inverse of the encoder for fixed-offset and DST zones alike.
 */
const storeLocalDay = (store: StoreWithTimezone | undefined, date: Date): string => {
  if (!store || !hasTimezoneUtc(store.timezone)) {
    return format(date, 'yyyy-MM-dd')
  }
  return DateTime.fromJSDate(date).setZone(store.timezone.utc[0]).toFormat('yyyy-MM-dd')
}

/**
 * `storeLocalDay` plus the storeId -> store lookup, for callers that hold an id rather than the
 * store record (the report screens hold the record; a spending holds its store's id).
 */
export const getStoreLocalDayString = (
  storeId: string | null = null,
  date: Date = new Date()
): string => {
  const jotaiStore = getDefaultStore()
  let store: StoreWithTimezone | undefined
  if (!storeId) {
    store = jotaiStore.get(userStoreAtom) as StoreWithTimezone | undefined
  } else {
    const storesDict = jotaiStore.get(storesDictAtom)
    store = storesDict[storeId] as StoreWithTimezone | undefined
  }
  return storeLocalDay(store, date)
}

// return date in store timezone
export const getLocalStoreDate = (storeId: string | null = null, date: Date = new Date()): Date => {
  const jotaiStore = getDefaultStore()
  let store: StoreWithTimezone | undefined
  if (!storeId) {
    store = jotaiStore.get(userStoreAtom) as StoreWithTimezone | undefined
  } else {
    const storesDict = jotaiStore.get(storesDictAtom)
    store = storesDict[storeId] as StoreWithTimezone | undefined
  }
  return localDateInStore(store, date)
}

interface DateRange {
  startDate: Date
  endDate: Date
}

/**
 * Which store frames a report range's edges, and by how much autoclose moves them.
 *
 * `dateRangeWithTimezoneAndAutoclose` floors the start in the EARLIEST-offset store, ceils the
 * end in the LATEST-offset one, and shifts by the LARGEST autoclose in the set. Inverting a
 * range means undoing exactly that, so both directions read the selection from here rather than
 * each keeping its own copy — a decode that picked a different frame than the encode is how the
 * range ends up a day wide on either side (ATL-1112).
 */
interface RangeStoreFrames {
  count: number
  earliestOffset?: StoreWithTimezone
  latestOffset?: StoreWithTimezone
  autoCloseHours: number
}

const rangeStoreFrames = (storeIds: string[] | null = null): RangeStoreFrames => {
  const jotaiStore = getDefaultStore()
  const storesDict = (jotaiStore.get(storesDictAtom) || {}) as Record<string, unknown>
  const stores: StoreWithTimezone[] = []
  if (!storeIds) {
    stores.push(...(Object.values(storesDict) as StoreWithTimezone[]))
  } else {
    for (const id of storeIds) {
      stores.push(storesDict[id] as StoreWithTimezone)
    }
  }

  if (stores.length === 0) {
    return { count: 0, autoCloseHours: 0 }
  }

  let earliestOffset: StoreWithTimezone = stores[0]
  let latestOffset: StoreWithTimezone = stores[0]
  let latestAutoClose: StoreWithTimezone = stores[0]
  for (const store of stores) {
    const storeOffset =
      typeof store?.timezone === 'object' && store.timezone ? store.timezone.offset : undefined
    const earliestOffsetValue =
      typeof earliestOffset?.timezone === 'object' && earliestOffset.timezone
        ? earliestOffset.timezone.offset
        : undefined
    const latestOffsetValue =
      typeof latestOffset?.timezone === 'object' && latestOffset.timezone
        ? latestOffset.timezone.offset
        : undefined

    if (storeOffset !== undefined && earliestOffsetValue !== undefined && storeOffset < earliestOffsetValue) {
      earliestOffset = store
    }
    if (storeOffset !== undefined && latestOffsetValue !== undefined && storeOffset > latestOffsetValue) {
      latestOffset = store
    }
    if (
      (store?.autoCloseShiftTime?.value ?? 0) > (latestAutoClose?.autoCloseShiftTime?.value ?? 0)
    ) {
      latestAutoClose = store
    }
  }

  return {
    count: stores.length,
    earliestOffset,
    latestOffset,
    autoCloseHours: latestAutoClose?.autoCloseShiftTime?.value || 0,
  }
}

export const dateRangeWithTimezoneAndAutoclose = (
  dateRange: DateRange,
  storeIds: string[] | null = null
): DateRange => {
  // for start and end date - cover the autoclose date - add autoclose hours
  // for timezone - get the dates in local hours
  // set to stores local date and time 00:00 but in user's timezone
  // set to stores local date and time 23:59 but in user's timezone

  // Normalize bounds up front. Callers (e.g. SupplyDetail during the Firestore→Supabase
  // read) may pass a range whose start or end is null/undefined; the timezone branches
  // below must NOT deref the raw `dateRange.*` values or `null.getDate()` throws
  // (QARASAY-KASSA-2AJ). `new Date(null)` is epoch (not NaN), so guard on the raw value
  // being nullish as well as on an invalid parse, falling back to the other bound or now.
  let startDate = dateRange.startDate != null ? new Date(dateRange.startDate) : null
  let endDate = dateRange.endDate != null ? new Date(dateRange.endDate) : null
  if (startDate == null || isNaN(startDate.getTime())) {
    startDate = endDate != null && !isNaN(endDate.getTime()) ? new Date(endDate) : new Date()
  }
  if (endDate == null || isNaN(endDate.getTime())) {
    endDate = new Date(startDate)
  }

  const { count, earliestOffset, latestOffset, autoCloseHours } = rangeStoreFrames(storeIds)

  if (count > 0) {
    const earliestTz = typeof earliestOffset?.timezone === 'object' ? earliestOffset.timezone : null
    const latestTz = typeof latestOffset?.timezone === 'object' ? latestOffset.timezone : null

    if (earliestTz?.offset && hasTimezoneUtc(earliestTz)) {
      // set store local date the same as startDate date but time to 00:00
      startDate = DateTime.fromObject(
        {
          day: startDate.getDate(),
          month: startDate.getMonth() + 1,
          year: startDate.getFullYear(),
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0,
        },
        { zone: earliestTz.utc[0] }
      ).toJSDate()
    } else {
      startDate.setHours(0, 0, 0, 0)
    }

    if (latestTz?.offset && hasTimezoneUtc(latestTz)) {
      endDate = DateTime.fromObject(
        {
          day: endDate.getDate(),
          month: endDate.getMonth() + 1,
          year: endDate.getFullYear(),
          hour: 23,
          minute: 59,
          second: 59,
          millisecond: 999,
        },
        { zone: latestTz.utc[0] }
      ).toJSDate()
    } else {
      endDate.setHours(23, 59, 59, 999)
    }

    if (autoCloseHours) {
      if (count === 1) {
        startDate.setMinutes(startDate.getMinutes() + autoCloseHours * 60)
      }
      endDate.setMinutes(endDate.getMinutes() + autoCloseHours * 60)
    }
  }
  return { startDate, endDate }
}

/** Every shape a record's date field arrives in across the two stores. */
export type DateLike = Date | { toDate: () => Date } | string | number | null | undefined

/**
 * Return the store's *business day* for an instant, as a Date whose local calendar
 * day is the business day. It is the local store date, shifted back one day when the
 * local time falls before the store's autoclose cutoff (i.e. the post-midnight tail
 * that still belongs to the previous trading day).
 *
 * This is the same business-day calculation `getDailyStatID` uses to assign a sale to
 * its dailyStats doc, so reports can bucket dailyStats on the SAME basis as the write
 * path. Bucketing by the autoclose-naive `getLocalStoreDate` instead attributes
 * post-midnight (pre-cutoff) activity to the next calendar day, which zeroed the real
 * business day in the Sales report for UTC+ stores open past midnight (ATL-674).
 */
export const getStoreBusinessDate = (storeId: string | null = null, date: Date = new Date()): Date => {
  const jotaiStore = getDefaultStore()
  let store: StoreWithTimezone | undefined
  if (!storeId) {
    store = jotaiStore.get(userStoreAtom) as StoreWithTimezone | undefined
  } else {
    const storesDict = jotaiStore.get(storesDictAtom)
    store = storesDict[storeId] as StoreWithTimezone | undefined
  }
  return businessDateForStore(store, date)
}

/**
 * ATL-2001 — the instant range of the store's CURRENT business day (or the one `date` falls in):
 * from the store's cutoff on its business day to just before the next cutoff. This is the range
 * every "this shift" read must use — the shift's cashbox transactions, its employees' working
 * hours, the bonuses counted at close. Ranging over the device's calendar day instead
 * (`dateRangeWithTimezoneAndAutoclose({startDate: new Date(), endDate: new Date()})`) is correct
 * only before midnight: between midnight and the cutoff it is the NEXT business day, so a shift
 * closed at 00:04 with a 04:00 cutoff read none of its own day's records.
 *
 * The store is required: the day and the frames must come from the SAME store, and
 * `getStoreBusinessDate(null)` (the user's store) and `dateRangeWithTimezoneAndAutoclose(null)`
 * (every store) do not name the same one.
 *
 * @param storeId - Store ID
 * @param date - The instant whose business day is wanted (defaults to now)
 */
export const businessDayRangeForStore = (storeId: string, date: Date = new Date()): DateRange => {
  const businessDay = getStoreBusinessDate(storeId, date)
  return dateRangeWithTimezoneAndAutoclose({ startDate: businessDay, endDate: businessDay }, [storeId])
}

/**
 * `getStoreBusinessDate` for a caller that already holds the store RECORD (ATL-2001).
 *
 * This is the business-day rule itself; `getStoreBusinessDate` (and through it `getDailyStatID`,
 * the Firestore-mode daily-stats key) is only this plus the storeId -> store lookup. The Supabase
 * daily-stats fetch calls it with the store records it was handed, so both read modes decide
 * "which day is today for this store" with one function and cannot drift apart. A store with no
 * timezone reads the device's clock and one with no autoclose reads its local calendar day —
 * exactly what `getStoreBusinessDate` does for an unknown store id.
 */
export const businessDateForStore = (store: StoreWithTimezone | undefined, date: Date): Date => {
  const localStoreDate = localDateInStore(store, date)
  // if local time < autoclose cutoff, the instant belongs to the previous day
  if (store?.autoCloseShiftTime?.value) {
    const storeAutoCloseDate = new Date(localStoreDate)
    storeAutoCloseDate.setHours(0, 0, 0, 0)
    storeAutoCloseDate.setMinutes(storeAutoCloseDate.getMinutes() + store.autoCloseShiftTime.value * 60)
    if (localStoreDate < storeAutoCloseDate) {
      localStoreDate.setDate(localStoreDate.getDate() - 1)
    }
  }
  return localStoreDate
}

/** `businessDateForStore` as the `YYYY-MM-DD` a `date`-typed business-day bound is spelled in. */
export const businessDayStringForStore = (store: StoreWithTimezone | undefined, date: Date): string =>
  format(businessDateForStore(store, date), 'yyyy-MM-dd')

/**
 * The store BUSINESS DATES a report range covers, as `yyyy-MM-dd` — the exact inverse of
 * `dateRangeWithTimezoneAndAutoclose`.
 *
 * That function encodes a range of business days as *instants*: local midnight in the
 * earliest-offset store's timezone for the start, local end-of-day in the latest-offset store's
 * for the end, both shifted by the largest autoclose in the set. Those instants are the right
 * thing to compare against a timestamp column, and the wrong thing to send to an endpoint whose
 * bound is a calendar day — `daily_stats.date` and `sales.business_date` are `date` columns, and
 * Postgres casts an instant to `date` by discarding the time AND the offset:
 *
 *     select '2026-08-31T19:00:00.000Z'::date   ->   2026-08-31
 *
 * So for a UTC+ store the start of business day D arrives as D-1 and the report silently covers
 * an extra day — which is why a single day's P&L summed two days, and why asking for a FUTURE
 * day returned today's takings instead of nothing (ATL-1112).
 *
 * The inverse undoes precisely what the encoding did: subtract the autoclose shift that edge
 * carries, then read the calendar day in the frame that BUILT that edge. Inverting an edge in
 * any other frame re-opens the same defect a day at a time — asking each store for its own
 * business day and taking the widest answer turns "1 September, all stores" into
 * `2026-08-31 … 2026-09-02` for an org with two same-timezone stores one of which closes at
 * 04:00, because the multi-store start carries no autoclose shift for `getStoreBusinessDate` to
 * cancel and the end carries the largest one rather than each store's own.
 *
 * A bound that is absent yields `null` (an omitted filter); one that is present but unparseable
 * is stringified (`"Invalid Date"`), so the server rejects the request rather than the range
 * silently becoming unbounded.
 */
export const businessDateRangeStrings = (
  dateRange: { startDate?: Date | null; endDate?: Date | null },
  storeIds: string[] | null = null
): { startDate: string | null; endDate: string | null } => {
  const { count, earliestOffset, latestOffset, autoCloseHours } = rangeStoreFrames(storeIds)

  // Only a store the ENCODING would have used as a frame may be used to decode: it takes the
  // luxon branch on `offset && utc`, and falls back to the device's own clock otherwise —
  // exactly as `dateRangeWithTimezoneAndAutoclose` does when it floors with `setHours`.
  const encodingFrame = (store: StoreWithTimezone | undefined): StoreWithTimezone | undefined => {
    const tz = typeof store?.timezone === 'object' ? store?.timezone : null
    return tz?.offset && hasTimezoneUtc(tz) ? store : undefined
  }

  const resolve = (bound: Date | null | undefined, edge: 'start' | 'end'): string | null => {
    if (bound == null) return null
    const instant = bound instanceof Date ? bound : new Date(bound)
    if (isNaN(instant.getTime())) return String(bound)
    // With no stores loaded the encoding left the bounds untouched, so read them as they are.
    if (count === 0) return format(instant, 'yyyy-MM-dd')

    // The start is shifted by autoclose only for a single store; the end always is. Undo it the
    // way the encoder applied it — `setMinutes` on the device wall clock, not raw milliseconds —
    // so the two agree across a DST transition in the DEVICE's own zone too.
    const shiftHours = edge === 'start' ? (count === 1 ? autoCloseHours : 0) : autoCloseHours
    let unshifted = instant
    if (shiftHours) {
      unshifted = new Date(instant)
      unshifted.setMinutes(unshifted.getMinutes() - shiftHours * 60)
    }
    const frame = encodingFrame(edge === 'start' ? earliestOffset : latestOffset)
    return storeLocalDay(frame, unshifted)
  }

  return {
    startDate: resolve(dateRange.startDate, 'start'),
    endDate: resolve(dateRange.endDate, 'end'),
  }
}

/**
 * The dailyStats document id for a row that ALREADY carries its business day as a CALENDAR DATE.
 *
 * `getDailyStatID` is the wrong function for such a row and the difference is a whole day. It
 * takes an INSTANT and answers "which business day does this moment belong to?" — applying the
 * store's timezone and then its autoclose cutoff. A Supabase `daily_stats` row has had both
 * applied already, server-side: `get_daily_stats` groups on `sales.business_date` and on
 * `business_date_for(ts, tz, cutoff)`, which is the same rule
 * (`packages/supabase-types/schema/functions/business_date_for.sql`). Its `date` reaches the app
 * as the bare `YYYY-MM-DD` the column holds, which the api-client turns into UTC midnight of that
 * day — so feeding it back through `getDailyStatID` books it onto D−1 for every store whose
 * autoclose cutoff exceeds its UTC offset, and for every store west of Greenwich. Measured with
 * the real functions: business day 2026-09-01 for a UTC+5 store closing at 06:00 came back as
 * `31-8-2026-<store>`, and for a UTC−5 store as `31-8-2026-<store>`.
 *
 * That is the same trap `validateDailyStats` documents in `utils/validationFunctions.js`
 * ("AN EARLIER REVISION OF THIS BRANCH USED `getDailyStatID` HERE AND THAT WAS BACKWARDS"): when
 * the day is already decided, the id is its autoclose-NAIVE spelling — `d-M-y` of that very day —
 * which is exactly what `saveProductStockSnapshot` and every dailyStats writer produced for it.
 *
 * @param businessDay the row's business day: `YYYY-MM-DD`, a `Date`, or a Firestore-like
 *   `{ toDate() }`. A Date/TimestampLike is read in UTC, because that is the frame the api-client
 *   built it in from the `date` column — reading it in the device's zone is the very off-by-one
 *   this helper exists to remove.
 * @param storeId the FIRESTORE store id (ATL-1118) — the key space the documents use.
 */
export const dailyStatIDForBusinessDay = (
  businessDay: string | Date | { toDate: () => Date } | null | undefined,
  storeId: string | null | undefined
): string | null => {
  if (!storeId) {
    console.warn('dailyStatIDForBusinessDay: storeId is missing, cannot generate ID')
    return null
  }
  const day = businessDayParts(businessDay)
  if (!day) {
    console.warn('dailyStatIDForBusinessDay: business day is missing or unreadable, cannot generate ID')
    return null
  }
  return `${day.day}-${day.month}-${day.year}-${storeId}`
}

/** `YYYY-MM-DD` / `Date` / `{toDate()}` -> the calendar day it names, or null. */
const businessDayParts = (
  value: string | Date | { toDate: () => Date } | null | undefined
): { year: number; month: number; day: number } | null => {
  if (typeof value === 'string') {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
    if (!match) return null
    return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) }
  }
  const date =
    value instanceof Date
      ? value
      : value && typeof (value as { toDate?: unknown }).toDate === 'function'
        ? (value as { toDate: () => Date }).toDate()
        : null
  if (!date || Number.isNaN(date.getTime())) return null
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() }
}

/**
 * The business day a record NAMES, as a Date whose LOCAL calendar fields are that day (ATL-1116).
 *
 * The labelling/bucketing counterpart of `dailyStatIDForBusinessDay` above, which answers the same
 * question in document-id form. Both exist because a `daily_stats` row's `date` IS the business
 * day — `get_daily_stats` groups on `business_date_for(ts, tz, cutoff)` — so re-deriving one from
 * it converts twice: the date-only `YYYY-MM-DD` reaches the app as UTC midnight, and
 * `getLocalStoreDate` / `getStoreBusinessDate` then render THAT at the store's wall clock and test
 * it against the store's cutoff. The result agrees with the row only for a store east of Greenwich
 * whose cutoff falls before the hour it renders at; a store west of Greenwich reads a day early,
 * and so does a store closing at 05:30 (three in production).
 *
 * Returns null for anything that does not name a day, so the caller can fall back to the instant
 * transform — which is the RIGHT one for a Firestore `dailyStats` document, whose `date` is a
 * mutation instant rather than a business day.
 */
export const businessDayAsLocalDate = (
  businessDay: string | Date | { toDate: () => Date } | null | undefined
): Date | null => {
  const day = businessDayParts(businessDay)
  if (!day) return null
  return new Date(day.year, day.month - 1, day.day)
}

export const getDailyStatID = (storeId: string | null = null, date: Date = new Date()): string | null => {
  const jotaiStore = getDefaultStore()
  let resolvedStoreId = storeId
  if (!storeId) {
    const store = jotaiStore.get(userStoreAtom) as StoreWithTimezone | undefined
    if (!store || !store.id) {
      console.warn('getDailyStatID: store is undefined, cannot generate ID')
      return null
    }
    resolvedStoreId = store.id
  }
  // Ensure storeId is a string before concatenating
  if (!resolvedStoreId) {
    console.warn('getDailyStatID: storeId is undefined, cannot generate ID')
    return null
  }
  // Business day (autoclose-aware) — the single source of truth shared with reports.
  const businessDate = getStoreBusinessDate(storeId, date)
  return format(businessDate, 'd-M-y') + '-' + resolvedStoreId
}

/**
 * A record's date field as a Date, accepting every shape the two stores hand us: a JS Date
 * (Supabase read path), a Firestore Timestamp (`toDate()`), or an ISO string.
 *
 * Deliberately a local copy of `global/Firebase/types.ts`'s `toDate` rather than an import:
 * this module is a leaf utility the Firebase layer depends on, so importing back into it
 * would close a cycle and drag the whole Supabase-flag/atom graph into every test that
 * touches a timezone. Keep the two in step.
 */
export const asDate = (value: DateLike): Date | null => {
  // An unparseable value must read as "no usable date", not as a Date. `new Date('n/a')`
  // and a Timestamp built from a bad seconds value both yield an Invalid Date, which is
  // truthy — letting one through skips the refusal below and surfaces instead as a bare
  // `RangeError: Invalid time value` thrown by `format()` deep inside `getDailyStatID`.
  const usable = (d: Date): Date | null => (Number.isNaN(d.getTime()) ? null : d)
  if (!value) return null
  if (value instanceof Date) return usable(value)
  // A numeric epoch is accepted because `format()` accepts one: rejecting it would turn a
  // record that used to produce a valid id into an undeletable one.
  if (typeof value === 'string' || typeof value === 'number') return usable(new Date(value))
  if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
    // A Timestamp-like whose `toDate()` returns undefined/null, or throws, must read as "no
    // usable date" — not as `TypeError: Cannot read properties of undefined (reading
    // 'getTime')` raised from inside this helper. An unnamed deep crash is precisely what the
    // named refusal below exists to replace.
    try {
      const d = (value as { toDate: () => Date }).toDate()
      return d instanceof Date ? usable(d) : null
    } catch {
      return null
    }
  }
  return null
}

/**
 * The dailyStats doc id a record's amounts live under — for REVERSAL paths
 * (delete / undo / re-key), which must target the doc the WRITE path created.
 *
 * Write paths key the doc with `getDailyStatID(store, date)` and stamp the result on the
 * record as `dailyStatID`, so that stored id is preferred here.
 *
 * "Authoritative" would be too strong, and it is worth saying why rather than implying a
 * guarantee this does not have. `markSalePaid` (firebase.ts) spreads its `paymentInfo` — whose
 * `dailyStatID` is the PAYMENT day (SaleDetailScreen) — straight onto the sale document, so a
 * post-paid sale's stored id ends up naming a different day than the one `saveNewSale` put its
 * `saleAmount`/`salesPurchaseAmount` into. Preferring the stored id then reverses those amounts
 * into the payment day. That predates this helper and is unchanged by it — the expression this
 * replaced preferred the same stored id — but it means the stored id is the best available
 * answer, not a proven one. Tracked as ATL-1125.
 * Records written before that field existed (and imported ones) carry no `dailyStatID`;
 * every reversal site used to hand-roll `format(date, 'd-M-y') + '-' + store` for those,
 * which is the DEVICE's calendar day and autoclose-naive. It disagrees with the write path
 * for exactly the instants ATL-674 was about — the post-midnight autoclose tail — and again
 * for any device outside the store's timezone.
 *
 * A mis-keyed reversal is silent and permanent: if the named doc exists the wrong day is
 * decremented while the right day stays inflated (across a month boundary that leaves a
 * MONTH total carrying a deleted record's amounts — ATL-1013); if it does not exist,
 * `tx.update` raises NOT_FOUND and aborts the whole delete transaction, so the record can
 * never be deleted at all.
 *
 * `date` is taken RAW rather than pre-converted by the caller. The call sites used to read
 * `info.saleDate.toDate()` inline, behind a `dailyStatID ||` short-circuit, so that shape
 * assumption was only ever evaluated when the stored id was missing. Passing the field
 * itself keeps that laziness and lets ONE place handle both stores' date shapes.
 *
 * Throws when there is neither a stored id nor a usable date: this is a reversal, and
 * guessing a doc id (today's, say) would silently decrement a day the record never touched.
 * Every call site that hand-rolled `.toDate()` already threw in that case — this only names
 * the record. Falls back to the old device-local expression when the record names NO store;
 * `getDailyStatID` is not consulted then (see below), and there is nothing better to compute.
 *
 * ⚠️ KNOWN LIMIT, measured, deliberately not fixed here. The recomputation runs through
 * `getStoreBusinessDate` → `localDateInStore`, which derives the store's offset from
 * `DateTime.local()` — the offset in effect NOW, not at the record's instant (the same flaw
 * `storeLocalDay` above was built to avoid, and which ATL-1112 fixed for RANGE bounds only).
 * So for a DST-observing store the recomputed id depends on WHEN the delete happens:
 * store `America/New_York`, sale at 2026-07-01T04:30:00Z (00:30 EDT), deleted in July →
 * `1-7-2026`; the same sale deleted in January → `30-6-2026`. 35 stores across 31 live orgs
 * are in DST zones; the exposed window is the hour after the store's cutoff.
 *
 * Not fixed here because the obviously-correct-looking fix is not obviously correct. This
 * function must reproduce the doc the WRITE actually created, and the write called
 * `getDailyStatID` with the SAME now-offset flaw at write time. For a real-time sale that
 * agrees with the instant (now ≈ the sale), so instant-correct maths would MATCH and is
 * right; for a record BACKDATED across a DST boundary the write already landed on the
 * now-offset day, and instant-correct maths would MISS the doc it created. Choosing between
 * those populations, and changing `getStoreBusinessDate` (which also buckets reports, and
 * which ATL-1112 has just rewritten), is a bigger decision than this fix. Tracked as ATL-1124.
 *
 * This is a narrowing, not a regression: the expression replaced here was wrong for EVERY
 * store outside the device's timezone and for every autoclose tail. What remains is one
 * hour, in DST zones, on records that carry no stored id.
 *
 * `getDailyStatID` is only consulted when the record NAMES a store. Called with a null
 * storeId it substitutes the logged-in user's current store, which is the same class of
 * guess this function refuses above: a record whose `store` did not survive the read (both
 * `deleteSpending` and `deleteCashboxTransaction` take an untyped `info`) would be reversed
 * out of the CASHIER's store — a valid doc id for the wrong store's books, silently. The
 * pre-fix expression produced `…-undefined`, which named a doc that does not exist and
 * aborted the transaction loudly; that loudness is preserved.
 */
/**
 * `dailyStatIDForRecord` for call sites that must SKIP rather than abort.
 *
 * A few reversals are deliberately degrade-not-abort — `deleteManufacture` says so in its own
 * comment, because the Supabase delete RPC recomputes the stats, so a missing Firestore stats
 * write is recoverable while a failed delete leaves stock unreversed. Those sites need "no
 * usable id" as a VALUE, not as an exception; pre-checking the date by truthiness does not give
 * them that, because a truthy-but-unparseable date still reaches the refusal.
 */
export const dailyStatIDForRecordOrNull = (
  recordDailyStatID: string | null | undefined,
  storeId: string | null | undefined,
  date: DateLike,
): string | null => {
  if (recordDailyStatID) return recordDailyStatID
  if (!storeId || !asDate(date)) return null
  return dailyStatIDForRecord(recordDailyStatID, storeId, date)
}

export const dailyStatIDForRecord = (
  recordDailyStatID: string | null | undefined,
  storeId: string | null | undefined,
  date: DateLike,
): string => {
  if (recordDailyStatID) return recordDailyStatID
  const resolved = asDate(date)
  if (!resolved) {
    throw new Error(
      `dailyStatIDForRecord: record for store "${storeId}" has no dailyStatID and no usable date — refusing to guess which day to reverse`,
    )
  }
  // With a non-empty storeId `getDailyStatID` cannot return null, so the fallback below is
  // the no-store case ONLY — and it deliberately yields `…-undefined`/`…-null`, a doc that
  // does not exist, rather than a plausible id for the wrong store's books.
  const businessDayId = storeId ? getDailyStatID(storeId, resolved) : null
  return businessDayId || format(resolved, 'd-M-y') + '-' + storeId
}

/** Hour used for a date-only import cell, before the autoclose clamp below. */
const IMPORTED_DATE_DEFAULT_HOUR = 12

/**
 * Build the UTC instant for a wall-clock time on a given calendar day IN THE STORE'S
 * TIMEZONE. Used by the sales import to turn a spreadsheet date into a `saleDate`.
 *
 * Two hazards this exists to avoid:
 *
 * 1. `new Date(y, m, d)` builds the instant in the *device's* timezone, so a sale
 *    imported from a phone in another zone would land on a different store day.
 * 2. `getDailyStatID` applies the store's autoclose cutoff: for a store closing at 04:00,
 *    an instant at 00:00 local belongs to the PREVIOUS business day. A date-only cell
 *    stamped midnight would therefore be booked, and reported, on the wrong day. So a
 *    date-only cell is placed at midday, and pushed past the cutoff on the rare store
 *    whose autoclose is at or after midday.
 *
 * @param storeId store whose timezone and autoclose apply
 * @param parts   calendar date, plus a wall-clock time when the file supplied one
 */
export const storeLocalDateAtTime = (
  storeId: string | null,
  parts: {
    year: number
    month: number
    day: number
    hasTime?: boolean
    hour?: number
    minute?: number
    second?: number
  }
): Date => {
  const jotaiStore = getDefaultStore()
  let store: StoreWithTimezone | undefined
  if (!storeId) {
    store = jotaiStore.get(userStoreAtom) as StoreWithTimezone | undefined
  } else {
    const storesDict = jotaiStore.get(storesDictAtom)
    store = storesDict[storeId] as StoreWithTimezone | undefined
  }

  let hour = parts.hour ?? 0
  let minute = parts.minute ?? 0
  let second = parts.second ?? 0

  if (!parts.hasTime) {
    const autoCloseHour = store?.autoCloseShiftTime?.value ?? 0
    hour = autoCloseHour >= IMPORTED_DATE_DEFAULT_HOUR
      ? Math.min(autoCloseHour + 1, 23)
      : IMPORTED_DATE_DEFAULT_HOUR
    minute = 0
    second = 0
  }

  const timezone = store?.timezone
  const zone = hasTimezoneUtc(timezone) ? timezone.utc[0] : null
  const dateTime = DateTime.fromObject(
    { year: parts.year, month: parts.month, day: parts.day, hour, minute, second },
    zone ? { zone } : undefined
  )
  return dateTime.toJSDate()
}

export const getMonthlyStatDocID = (storeId: string | null = null, date: Date = new Date()): string => {
  const localStoreDate = getLocalStoreDate(storeId, date)
  const jotaiStore = getDefaultStore()
  let store: StoreWithTimezone | undefined
  let resolvedStoreId: string | null = storeId
  if (!storeId) {
    store = jotaiStore.get(userStoreAtom) as StoreWithTimezone | undefined
    resolvedStoreId = store?.id ?? null
  } else {
    const storesDict = jotaiStore.get(storesDictAtom)
    store = storesDict[storeId] as StoreWithTimezone | undefined
  }
  // if date hour < autocloseshift hour, then return startOfDay - 1 day
  if (store?.autoCloseShiftTime?.value) {
    const storeAutoCloseDate = new Date(localStoreDate)
    storeAutoCloseDate.setHours(0, 0, 0, 0)
    storeAutoCloseDate.setMinutes(storeAutoCloseDate.getMinutes() + store.autoCloseShiftTime.value * 60)
    if (localStoreDate < storeAutoCloseDate) {
      localStoreDate.setDate(localStoreDate.getDate() - 1)
    }
  }
  return format(localStoreDate, 'M-y') + '-' + (resolvedStoreId ?? '')
}
